// Single source of truth for the page-level tab strip, rendered in the global
// TopBar (app header). Ported from the old per-page AdministrationHeader switch
// so tabs live in one place. Returns [{ title, url, active, disabled }].

const seg = (pathname, i) => pathname.split("/")[i];

export function getHeaderTabs(location, params = {}) {
	const path = location.pathname;
	const search = location.search || "";
	const id = params.id || seg(path, 2) || "";

	// organize: organisations
	if (path === "/organize-organisations" || path === "/organisations-mapping") {
		return [
			{ title: "Elements", url: "/organize-organisations", active: path === "/organize-organisations" },
			{ title: "Tags", url: "/organisations-mapping", active: path === "/organisations-mapping" },
		];
	}

	// organize: products
	if (path === "/organize-products" || path === "/products-mapping-tags" || path === "/products-mapping-build-sources") {
		return [
			{ title: "Elements", url: "/organize-products", active: path === "/organize-products" },
			{ title: "Tags", url: "/products-mapping-tags", active: path === "/products-mapping-tags" },
			{ title: "Build sources", url: "/products-mapping-build-sources", active: path === "/products-mapping-build-sources" },
		];
	}

	// organize: components
	if (path === "/organize-components" || path === "/components-mapping") {
		return [
			{ title: "Elements", url: "/organize-components", active: path === "/organize-components" },
			{ title: "Mapping", url: "/components-mapping", active: path === "/components-mapping" },
		];
	}

	// reports
	if (path === "/report-list" || path.substring(0, 14) === "/report-editor") {
		return [
			{ title: "Report list", url: "/report-list", active: path === "/report-list" },
			{ title: "Report", url: "/report-editor", active: path.substring(0, 14) === "/report-editor", disabled: path === "/report-list", detail: true },
		];
	}

	// Cloud & AI Spend — Cost & Usage Explorer and AI Usage Monitor are two views of
	// the same section (AI Usage Monitor was promoted from its own sidebar item to a tab).
	if (path === "/cost-analyzer" || path === "/ai-monitor") {
		return [
			{ title: "Cost & Usage", url: "/cost-analyzer", active: path === "/cost-analyzer" },
			{ title: "AI Usage", url: "/ai-monitor", active: path === "/ai-monitor" },
		];
	}

	// economic control family — Control · Enforcement · Ownership · Forecast.
	// "Enforcement" is the budget-enforcement guardrails cockpit (/budget-enforcement).
	if (
		path === "/financial-control" ||
		path === "/budget-enforcement" ||
		path === "/ownership-accountability" ||
		path === "/forecast-predictability"
	) {
		return [
			{ title: "Overview", url: "/financial-control", active: path === "/financial-control" },
			{ title: "Budget Performance / Enforcement", url: "/budget-enforcement", active: path === "/budget-enforcement" },
			{ title: "Ownership Accountability", url: "/ownership-accountability", active: path === "/ownership-accountability" },
			{ title: "Forecast Risk", url: "/forecast-predictability", active: path === "/forecast-predictability" },
		];
	}

	// forecast lab (sub-views URL-driven via ?tab=). Model Ops is NOT listed here — it
	// belongs to the System Configuration group below, so tab=model-ops falls through.
	if (path === "/forecast-lab") {
		const labTab = new URLSearchParams(search).get("tab") || "explorer";
		if (labTab !== "model-ops") {
			return [
				{ title: "Forecast Explorer", url: "/forecast-lab?tab=explorer", active: labTab === "explorer" },
				{ title: "Model Governance", url: "/forecast-lab?tab=model-studio", active: labTab === "model-studio" },
				{ title: "Scenarios", url: "/forecast-lab?tab=scenario", active: labTab === "scenario" },
				{ title: "Accuracy", url: "/forecast-lab?tab=calibration", active: labTab === "calibration" },
			];
		}
	}

	// untagged
	if (path === "/untagged-overview" || path === "/untagged-cost-resources" || path === "/untagged-resources") {
		return [
			{ title: "Overview", url: "/untagged-overview", active: path === "/untagged-overview" },
			{ title: "Cost Resources", url: "/untagged-cost-resources", active: path === "/untagged-cost-resources" },
			{ title: "Resources", url: "/untagged-resources", active: path === "/untagged-resources" },
		];
	}

	// budgets. The list (/budgets) is filtered by status via ?status=, surfaced as the
	// tab strip (All / Draft / Active / Paused / Archived). A specific budget
	// (/budgets/:id) is a record — not a tab (its name is the title). The new-budget
	// wizard (/budgets/new) has no strip (CockpitHeader supplies "Create budget").
	if (path === "/budgets" || path.startsWith("/budgets/")) {
		if (path === "/budgets/new") return [];
		if (path.startsWith("/budgets/")) {
			return [
				{ title: "Budgets", url: "/budgets", active: false },
				{ title: "Budget", url: path, active: true, disabled: false, detail: true },
			];
		}
		const status = new URLSearchParams(search).get("status") || "all";
		return [
			{ title: "Current", url: "/budgets", active: status === "all" },
			{ title: "Draft", url: "/budgets?status=draft", active: status === "draft" },
			{ title: "Active", url: "/budgets?status=active", active: status === "active" },
			{ title: "Paused", url: "/budgets?status=paused", active: status === "paused" },
			{ title: "Archived", url: "/budgets?status=archived", active: status === "archived" },
		];
	}

	// invoices — the "Invoice" tab is only enabled while a specific invoice is open
	// (/invoice-review/:id), not the queue list or the automation-settings page.
	if (path === "/invoice-review" || path.startsWith("/invoice-review/")) {
		const invoiceDetail = path.startsWith("/invoice-review/") && path !== "/invoice-review/settings";
		return [
			{ title: "Invoices", url: "/invoice-review", active: path === "/invoice-review" },
			// Detail (single invoice) is not a tab — see the budgets note above.
			{ title: "Invoice", url: invoiceDetail ? path : "/invoice-review", active: invoiceDetail, disabled: !invoiceDetail, detail: true },
		];
	}

	// approvals — the "Approval" tab is only enabled while a specific request is shown
	// (/approvals/:id), not the inbox list.
	if (path === "/approvals" || path.startsWith("/approvals/")) {
		const approvalDetail = path.startsWith("/approvals/");
		return [
			{ title: "Approvals", url: "/approvals", active: path === "/approvals" },
			{ title: "Approval", url: approvalDetail ? path : "/approvals", active: approvalDetail, disabled: !approvalDetail, detail: true },
		];
	}

	// investigations. The list (/investigations) is filtered via ?filter=, surfaced
	// as the tab strip (Open / My investigations / Closed) — same pattern as the
	// budgets status tabs. A specific investigation (/investigations/:id) is a
	// record: not a tab (its title carries the identity), with an enabled-only-here
	// detail tab.
	if (path === "/investigations" || path.startsWith("/investigations/")) {
		if (path.startsWith("/investigations/")) {
			return [
				{ title: "Investigations", url: "/investigations", active: false },
				{ title: "Investigation", url: path, active: true, disabled: false, detail: true },
			];
		}
		const filter = new URLSearchParams(search).get("filter") || "open";
		return [
			{ title: "Open", url: "/investigations", active: filter === "open" },
			{ title: "My investigations", url: "/investigations?filter=mine", active: filter === "mine" },
			{ title: "Closed", url: "/investigations?filter=closed", active: filter === "closed" },
		];
	}

	// board reports — the "Report" tab is only enabled while a specific report is shown
	// (/board-reports/:id and its children: home tabs, runs, review, preview, template), not the
	// list or the create form (/board-reports/new/config). Same disabled-until-detail pattern as
	// Budgets / Approvals / Investigations. AdministrationHeader renders no visible title on these
	// routes, so this strip is what shows the title in the TopBar.
	if (path === "/board-reports" || path.startsWith("/board-reports/")) {
		const reportId = seg(path, 2);
		const reportDetail = path.startsWith("/board-reports/") && reportId !== "" && reportId !== "new";
		return [
			{ title: "Board Reports", url: "/board-reports", active: path === "/board-reports" },
			{ title: "Report", url: reportDetail ? `/board-reports/${reportId}` : "/board-reports", active: reportDetail, disabled: !reportDetail, detail: true },
		];
	}

	// source connections — the "Source Connection" tab is only enabled while a specific
	// connection is shown (/source-connections/:id), not the list.
	if (path === "/source-connections" || path.startsWith("/source-connections/")) {
		const connectionDetail = path.startsWith("/source-connections/");
		return [
			{ title: "Source Connections", url: "/source-connections", active: path === "/source-connections" },
			{ title: "Source Connection", url: connectionDetail ? path : "/source-connections", active: connectionDetail, disabled: !connectionDetail, detail: true },
		];
	}

	// cost allocation / structure
	if (path === "/cost-allocation" || path === "/cost-allocation/hierarchy" || path === "/organisational-structure" || path === "/tag-configuration" || path === "/virtual-tags") {
		return [
			{ title: "Allocation Hierarchy", url: "/cost-allocation/hierarchy", active: path === "/cost-allocation/hierarchy" },
			{ title: "Virtual Tags", url: "/virtual-tags", active: path === "/virtual-tags" },
		];
	}

	// data pipeline (its own sidebar item): pipeline, reporting jobs and activity log.
	// "Data Job" is only enabled while a specific pipeline job is shown (/data-pipeline/:id,
	// reached by clicking a job in the pipeline), not the list or the activity-log detail.
	if (path.startsWith("/data-pipeline") || path.startsWith("/reporting-jobs")) {
		const jobDetail = path.startsWith("/data-pipeline/") && !path.startsWith("/data-pipeline/log");
		// One activity-log entry (/data-pipeline/log/:id) is a RECORD, exactly like
		// /budgets/:id: its identity belongs in the title and the breadcrumb, not in a tab
		// strip, and it gets the back chevron every other record screen has. Returning only
		// the `detail` tab is what produces both — TopBar filters it out, leaving no strip,
		// and an active detail tab is the flag the chevron keys on.
		if (path.startsWith("/data-pipeline/log/")) {
			return [{ title: "Activity", url: path, active: true, detail: true }];
		}
		// On a specific pipeline job (/data-pipeline/:id) the record's identity lives in the
		// title/breadcrumb, so show no tab strip (return only the `detail` tab — TopBar
		// filters it out, leaving zero visible tabs).
		if (jobDetail) {
			return [{ title: "Data Job", url: path, active: true, detail: true }];
		}
		return [
			{ title: "Data Pipeline", url: "/data-pipeline", active: path === "/data-pipeline" && !search.includes("tab=activity") },
			{ title: "Reporting Jobs", url: "/reporting-jobs", active: path.startsWith("/reporting-jobs") },
			{ title: "Activity Log", url: "/data-pipeline?tab=activity", active: search.includes("tab=activity") || path.startsWith("/data-pipeline/log") },
		];
	}

	// access management (its own sidebar item): users + ingest tokens. The "User" tab is only
	// enabled while a specific user is shown (/settings/users/:id), not the list or new-user form.
	if (path === "/users-configuration" || path.startsWith("/settings/users") || path.startsWith("/litellm-ingest-tokens")) {
		const userDetail = path.startsWith("/settings/users/") && path !== "/settings/users/new";
		// A specific user (/settings/users/:id) is a record — its identity lives in
		// the title, not a tab. Return only the list + the `detail` tab so no strip
		// shows (same pattern as /budgets/:id, /investigations/:id, /approvals/:id).
		if (userDetail) {
			return [
				{ title: "Users", url: "/settings/users", active: false },
				{ title: "User", url: path, active: true, disabled: false, detail: true },
			];
		}
		return [
			{ title: "Users", url: "/settings/users", active: path === "/settings/users" || path === "/settings/users/new" || path === "/users-configuration" },
			{ title: "User", url: "/settings/users", active: false, disabled: true, detail: true },
			{ title: "Ingest Tokens", url: "/litellm-ingest-tokens", active: path.startsWith("/litellm-ingest-tokens") },
		];
	}

	// System Configuration (Admin) — the config pages plus Model Ops, which lives in the
	// forecast-lab page (?tab=model-ops) but is grouped under System Configuration.
	const isModelOps = path === "/forecast-lab" && new URLSearchParams(search).get("tab") === "model-ops";
	if (
		isModelOps ||
		path === "/slack-configuration" ||
		path.startsWith("/cost-source-taxonomy") || path.startsWith("/provider-mappings") ||
		path.startsWith("/application-parameters")
	) {
		return [
			{ title: "Cost Taxonomy", url: "/cost-source-taxonomy", active: path.startsWith("/cost-source-taxonomy") },
			{ title: "Provider Mappings", url: "/provider-mappings", active: path.startsWith("/provider-mappings") },
			{ title: "Application Parameters", url: "/application-parameters", active: path.startsWith("/application-parameters") },
			{ title: "Model Ops", url: "/forecast-lab?tab=model-ops", active: isModelOps },
		];
	}

	// Cost Sources (Setup). On a specific source builder (/allocation/sources/:id or
	// /new) the record's identity lives in the title/breadcrumb, so show no tab strip
	// (return only the `detail` tab — TopBar filters it out, leaving zero visible tabs).
	if (path === "/allocation" || path.startsWith("/allocation/") || path === "/detected-sources") {
		if (path.startsWith("/allocation/sources/")) {
			return [{ title: "Source Detail", url: path, active: true, detail: true }];
		}
		return [
			{ title: "Sources", url: "/allocation", active: path === "/allocation" },
			{ title: "Detected Sources", url: "/detected-sources", active: path === "/detected-sources" },
			{ title: "Naming Rules", url: "/allocation/names", active: path === "/allocation/names" },
		];
	}

	// Policies & Guardrails (Decisions) — one section, three pages as tabs: approval
	// policies, delegations, and budget guardrails (the budget-enforcement policies).
	// Merges the former Control Policies + Budget Policies groups so the single sidebar
	// item reaches all three (the detail routes /:id are opened by row-click within each).
	if (path.startsWith("/control-policies/") || path.startsWith("/budget-enforcement/policies")) {
		return [
			{ title: "Approval Policies", url: "/control-policies/approval-policies", active: path.startsWith("/control-policies/approval-policies") },
			{ title: "Delegations", url: "/control-policies/delegations", active: path === "/control-policies/delegations" },
			{ title: "Budget Guardrails", url: "/budget-enforcement/policies", active: path.startsWith("/budget-enforcement/policies") },
		];
	}

	// resources
	if (path === "/resources" || path === "/resource-changes" || path.substring(0, 9) === "/resource") {
		return [
			{ title: "List of resources", url: "/resources", active: path === "/resources" },
			{ title: "Resource changes", url: "/resource-changes", active: path === "/resource-changes" },
			{
				title: "Resource details",
				url: path.substring(0, 9) === "/resource" ? path : "/resources",
				active: path !== "/resources" && path !== "/resource-changes" && path.substring(0, 9) === "/resource",
				disabled: path === "/resources" || path === "/resource-changes",
				detail: true,
			},
		];
	}

	// products
	if (path === "/product-list" || path.substring(0, 17) === "/product-overview" || path.substring(0, 18) === "/product-resources" || path.substring(0, 21) === "/product-cost-details") {
		const enc = encodeURIComponent(id);
		return [
			{ title: "Product list", url: "/product-list", active: path === "/product-list" },
			{ title: "Product overview", url: "/product-overview/" + enc, active: path.substring(0, 17) === "/product-overview", disabled: path === "/product-list" },
			{ title: "Product resources", url: "/product-resources/" + enc, active: path.substring(0, 18) === "/product-resources", disabled: path === "/product-list" },
			{ title: "Cost details", url: "/product-cost-details/" + enc, active: path.substring(0, 21) === "/product-cost-details", disabled: path === "/product-list" },
		];
	}

	// organization levels
	if (path.substring(0, 15) === "/organization-l" || path === "/allocation-details" || path === "/organisational-cost-overview" || path === "/organisational-overview") {
		return [
			{ title: "All Organisation", url: "/organisational-overview", active: path === "/organisational-overview" },
			{ title: "Board of Directors", url: "/organization-l1-overview", active: path === "/organization-l1-overview" },
			{ title: "Division", url: "/organization-l2-overview", active: path === "/organization-l2-overview" },
			{ title: "Department", url: "/organization-l3-overview", active: path === "/organization-l3-overview" },
			{ title: "Level Overview", url: "/organisational-cost-overview", active: path === "/organisational-cost-overview" },
			{ title: "Allocation Details", url: "/allocation-details", active: path === "/allocation-details" },
		];
	}

	// Use Cases — the detail page's tabs. Tabs are path segments because this
	// strip renders from the pathname.
	if (path.startsWith("/use-cases/")) {
		const nodeId = seg(path, 2);
		const base = `/use-cases/${nodeId}`;
		return [
			{ title: "Overview", url: base, active: path === base },
			{ title: "Financials", url: `${base}/financials`, active: path === `${base}/financials` },
			{ title: "Value & ROI", url: `${base}/value`, active: path === `${base}/value` },
			{ title: "Usage", url: `${base}/usage`, active: path === `${base}/usage` },
		];
	}

	return [];
}
